import type { Manifest } from '../core/types.ts';

/**
 * The built-in registry. Every entry pins an exact Hugging Face commit so a
 * model ID never changes meaning. Sizes are the download for the listed dtype.
 *
 * Status rules: `stable` models have passing real-model tests on Bun, Node and
 * Chromium in this repository. Everything else is `preview` or `experimental`.
 */
const MB = 1e6;
/** `memory` values are measured in Chromium: resident memory the loaded model adds. */
const GB = 1e9;
const ALL = ['webgpu', 'wasm', 'cpu'] as const;
const CPU = ['wasm', 'cpu'] as const;

const all = () => [...ALL];
const cpu = () => [...CPU];

export const builtinManifests: Manifest[] = [
  // ---------------------------------------------------------------- generate: text
  {
    id: 'lfm2.5-230m',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['text'],
    task: 'causal-lm',
    source: { repo: 'LiquidAI/LFM2.5-230M-ONNX', revision: 'c6f46e4e3f885ebcad164d14059a49f90e27eb4d' },
    // The q4 export uses GatherBlockQuantized, which ONNX Runtime's WebAssembly build lacks.
    variants: [
      { dtype: 'q4', devices: ['webgpu', 'cpu'], bytes: 211 * MB },
      {
        dtype: 'q4',
        devices: ['wasm'],
        bytes: 211 * MB,
        files: {
          bucket: 'kucukkanat/edgewise-models',
          path: 'lfm2.5-230m/wasm-q4/v1',
          names: ['model_q4.onnx', 'model_q4.onnx_data'],
          sha256: {
            'model_q4.onnx': '984dd5cc416aac277ef2f4ac943ee3431f89c111a00ac78cf7da63c60ce05091',
            'model_q4.onnx_data': 'b51a4580a88a2cd0486032cfdaf8694b09359abca5a97df8deb0a314ea6e5b34',
          },
        },
        memory: 0.85 * GB,
      },
    ],
    params: '230M',
    license: 'lfm1.0',
    status: 'stable',
    features: ['stream', 'json', 'tools'],
    config: { toolFormat: 'lfm2' },
    description: 'Smallest LFM2.5 language model. Rewrites, extraction, short answers.',
  },
  {
    id: 'lfm2.5-350m',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['text'],
    task: 'causal-lm',
    source: { repo: 'LiquidAI/LFM2.5-350M-ONNX', revision: 'd11593fd9eb408e322667926656598896c2d5ff9' },
    variants: [
      { dtype: 'q4f16', devices: ['webgpu'], shaderF16: true, bytes: 255 * MB },
      { dtype: 'q4', devices: ['webgpu', 'cpu'], bytes: 294 * MB, memory: { webgpu: 1.1 * GB } },
      // WebAssembly: the q4 export with its quantized embedding rewritten into standard ops (bit-identical),
      // because ONNX Runtime Web's WASM build lacks GatherBlockQuantized. fp16 used ~3.2 GB; this ~0.65 GB.
      {
        dtype: 'q4',
        devices: ['wasm'],
        bytes: 294 * MB,
        files: {
          bucket: 'kucukkanat/edgewise-models',
          path: 'lfm2.5-350m/wasm-q4/v1',
          names: ['model_q4.onnx', 'model_q4.onnx_data'],
          sha256: {
            'model_q4.onnx': '2025d575d3a308d696474914b6d407514e8bcd50b2432240a51754f26fe2fb5f',
            'model_q4.onnx_data': '71ec6ad38a4c463dcb3dba671d06a1d9861be3a23e51290d818b95c0b7d2a5db',
          },
        },
        memory: 0.7 * GB,
      },
    ],
    params: '350M',
    license: 'lfm1.0',
    status: 'stable',
    features: ['stream', 'json', 'tools'],
    config: { toolFormat: 'lfm2' },
    description: 'Fast general model with native tool calling.',
  },
  {
    id: 'lfm2.5-1.2b',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['text'],
    task: 'causal-lm',
    source: { repo: 'LiquidAI/LFM2.5-1.2B-Instruct-ONNX', revision: '10f72e70abf67ac0fd7ebf15bc5854726891d864' },
    variants: [
      { dtype: 'q4f16', devices: ['webgpu'], shaderF16: true, bytes: 760 * MB },
      { dtype: 'q4', devices: ['webgpu', 'cpu'], bytes: 850 * MB },
    ],
    params: '1.2B',
    license: 'lfm1.0',
    status: 'preview',
    features: ['stream', 'json', 'tools'],
    config: { toolFormat: 'lfm2' },
    description: 'General chat and reasoning. Needs WebGPU in browsers.',
  },
  {
    id: 'qwen3-0.6b',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['text'],
    task: 'causal-lm',
    source: { repo: 'onnx-community/Qwen3-0.6B-ONNX', revision: 'da1453100cf3ff33ef56d17983fc7a8648706db6' },
    variants: [
      { dtype: 'q4f16', devices: ['webgpu'], shaderF16: true, bytes: 570 * MB },
      { dtype: 'q4', devices: all(), bytes: 919 * MB, memory: { wasm: 4.9 * GB } },
    ],
    params: '0.6B',
    license: 'apache-2.0',
    status: 'preview',
    features: ['stream', 'json', 'tools'],
    config: { toolFormat: 'hermes', chatTemplateOptions: { enable_thinking: false } },
    description: 'Multilingual model with an optional reasoning mode.',
  },
  {
    id: 'gemma-3-270m',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['text'],
    task: 'causal-lm',
    source: { repo: 'onnx-community/gemma-3-270m-it-ONNX', revision: '2dbbfdb1b59bd034eb959428c6a7da9dd7ea27f0' },
    variants: [
      { dtype: 'fp32', devices: ['webgpu'], bytes: 1140 * MB },
      { dtype: 'q4', devices: ['cpu'], bytes: 323 * MB },
      {
        dtype: 'q4',
        devices: ['wasm'],
        bytes: 323 * MB,
        files: {
          bucket: 'kucukkanat/edgewise-models',
          path: 'gemma-3-270m/wasm-q4/v1',
          names: ['model_q4.onnx', 'model_q4.onnx_data'],
          sha256: {
            'model_q4.onnx': 'a605587a912bf8dce67aba48b9637baf060edd552998d779bd18f1d2abe38f15',
            'model_q4.onnx_data': '25c4a14af32fc1011a90504076024b0e1f65f915e9690b44a5484200d2e20851',
          },
        },
        memory: 1.1 * GB,
      },
    ],
    params: '270M',
    license: 'gemma',
    status: 'preview',
    features: ['stream', 'json'],
    config: {},
    description: 'Tiny Gemma. Uses fp32 on WebGPU because fp16 overflows on that backend.',
  },
  {
    id: 'functiongemma-270m',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['text'],
    task: 'causal-lm',
    source: { repo: 'onnx-community/functiongemma-270m-it-ONNX', revision: 'ba3c872ede162a5c4ab753f509b2260af5587143' },
    variants: [
      { dtype: 'q4f16', devices: ['webgpu'], shaderF16: true, bytes: 426 * MB },
      { dtype: 'fp32', devices: all(), bytes: 1140 * MB, memory: { wasm: 4 * GB } },
    ],
    params: '270M',
    license: 'gemma',
    status: 'preview',
    features: ['stream', 'tools'],
    config: { toolFormat: 'functiongemma' },
    description: 'Gemma trained for function calling. Best after fine-tuning on your own tools.',
  },
  {
    id: 'lfm2-1.2b-tool',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['text'],
    task: 'causal-lm',
    source: { repo: 'onnx-community/LFM2-1.2B-Tool-ONNX', revision: '1992998ab37ef9db120f1589181db465e0f037ad' },
    variants: [
      { dtype: 'q4f16', devices: ['webgpu'], shaderF16: true, bytes: 868 * MB },
      { dtype: 'q4', devices: all(), bytes: 1218 * MB },
    ],
    params: '1.2B',
    license: 'lfm1.0',
    status: 'preview',
    features: ['stream', 'tools'],
    config: { toolFormat: 'lfm2' },
    description: 'LFM2 tuned for tool use.',
  },
  // ---------------------------------------------------------------- generate: vision
  {
    id: 'lfm2.5-vl-450m',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['text', 'image', 'video'],
    task: 'image-text-to-text',
    source: { repo: 'LiquidAI/LFM2.5-VL-450M-ONNX', revision: '95c283d4497a56477a83177079fa6b7121abb1b1' },
    variants: [
      { dtype: { embed_tokens: 'fp16', vision_encoder: 'q4', decoder_model_merged: 'q4' }, devices: ['webgpu'], shaderF16: true, bytes: 675 * MB },
      { dtype: { embed_tokens: 'fp16', vision_encoder: 'q4', decoder_model_merged: 'q4' }, devices: ['cpu'], bytes: 675 * MB },
      { dtype: { embed_tokens: 'fp16', vision_encoder: 'q4', decoder_model_merged: 'fp16' }, devices: ['wasm'], bytes: 919 * MB },
    ],
    params: '450M',
    license: 'lfm1.0',
    status: 'stable',
    features: ['stream', 'json'],
    config: {},
    description: 'Small vision-language model. Questions about images, screenshots and short clips.',
  },
  {
    id: 'lfm2.5-vl-1.6b',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['text', 'image', 'video'],
    task: 'image-text-to-text',
    source: { repo: 'LiquidAI/LFM2.5-VL-1.6B-ONNX', revision: 'd0e00ca26cc42892d9f3c4380faa631927218209' },
    variants: [
      { dtype: { embed_tokens: 'fp16', embed_images: 'q4', decoder: 'q4' }, devices: ['webgpu'], shaderF16: true, bytes: 1755 * MB },
      { dtype: { embed_tokens: 'fp16', embed_images: 'q4', decoder: 'q4' }, devices: ['cpu'], bytes: 1755 * MB },
    ],
    params: '1.6B',
    license: 'lfm1.0',
    status: 'preview',
    features: ['stream', 'json'],
    config: {},
    description: 'Larger vision-language model for better answers on GPU devices.',
  },
  {
    id: 'smolvlm-256m',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['text', 'image'],
    task: 'image-text-to-text',
    source: { repo: 'HuggingFaceTB/SmolVLM-256M-Instruct', revision: '7e3e67edbbed1bf9888184d9df282b700a323964' },
    variants: [{ dtype: { embed_tokens: 'fp32', vision_encoder: 'q4', decoder_model_merged: 'q4' }, devices: all(), bytes: 265 * MB }],
    params: '256M',
    license: 'apache-2.0',
    status: 'preview',
    features: ['stream'],
    config: {},
    description: 'Tiny vision-language model.',
  },
  {
    id: 'florence-2-base',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['image', 'text'],
    task: 'florence2',
    source: { repo: 'onnx-community/Florence-2-base-ft', revision: 'e88a44eaf3791a35eae0c5a47b3dbcd36e67eb6f' },
    variants: [
      {
        dtype: { embed_tokens: 'fp32', vision_encoder: 'fp32', encoder_model: 'fp32', decoder_model_merged: 'q4' },
        devices: all(),
        bytes: 760 * MB,
      },
    ],
    params: '230M',
    license: 'mit',
    status: 'stable',
    features: ['presets'],
    config: {},
    description: 'Captions, OCR and object detection through presets.',
  },
  // ---------------------------------------------------------------- generate: audio
  {
    id: 'moonshine-tiny',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['audio'],
    task: 'speech-to-text',
    source: { repo: 'onnx-community/moonshine-tiny-ONNX', revision: 'a6da1241cd305dcd64eab1edbd615f2bb9aabb95' },
    variants: [
      { dtype: { encoder_model: 'fp32', decoder_model_merged: 'q4' }, devices: ['webgpu'], bytes: 76 * MB, memory: 0.9 * GB },
      { dtype: 'q8', devices: cpu(), bytes: 28 * MB, memory: { wasm: 0.4 * GB } },
    ],
    params: '27M',
    license: 'mit',
    status: 'stable',
    features: ['stream'],
    config: { language: 'en' },
    description: 'Tiny English speech recognizer for live captions on any device.',
  },
  {
    id: 'moonshine-base',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['audio'],
    task: 'speech-to-text',
    source: { repo: 'onnx-community/moonshine-base-ONNX', revision: 'b1e9b6aae3c3c7298f10c3798393fdf38e8fbbad' },
    variants: [
      { dtype: { encoder_model: 'fp32', decoder_model_merged: 'q4' }, devices: ['webgpu'], bytes: 154 * MB },
      { dtype: 'q8', devices: cpu(), bytes: 63 * MB },
    ],
    params: '61M',
    license: 'mit',
    status: 'stable',
    features: ['stream'],
    config: { language: 'en' },
    description: 'English speech recognizer with better accuracy than Moonshine tiny.',
  },
  {
    id: 'whisper-tiny-en',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['audio'],
    task: 'speech-to-text',
    source: { repo: 'onnx-community/whisper-tiny.en', revision: '2575352d61be1bf7225cf8f8b268a4678025fc58' },
    variants: [
      { dtype: { encoder_model: 'fp32', decoder_model_merged: 'q4' }, devices: ['webgpu'], bytes: 120 * MB },
      { dtype: 'q8', devices: cpu(), bytes: 41 * MB },
    ],
    params: '39M',
    license: 'mit',
    status: 'preview',
    features: ['stream', 'timestamps'],
    config: { language: 'en', whisper: true },
    description: 'Whisper tiny, English only, with timestamps.',
  },
  {
    id: 'whisper-base',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['audio'],
    task: 'speech-to-text',
    source: { repo: 'onnx-community/whisper-base', revision: '1846881b6b3a3024392c1eea3ad983695bc23925' },
    variants: [
      { dtype: { encoder_model: 'fp32', decoder_model_merged: 'q4' }, devices: ['webgpu'], bytes: 195 * MB },
      { dtype: 'q8', devices: cpu(), bytes: 73 * MB },
    ],
    params: '74M',
    license: 'mit',
    status: 'preview',
    features: ['stream', 'timestamps', 'languages'],
    config: { whisper: true, multilingual: true },
    description: 'Small multilingual transcription with timestamps.',
  },
  {
    id: 'whisper-small',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['audio'],
    task: 'speech-to-text',
    source: { repo: 'onnx-community/whisper-small', revision: '36050c46d777d46dc4b5f43f6d90574fc38f8732' },
    variants: [
      { dtype: { encoder_model: 'fp32', decoder_model_merged: 'q4' }, devices: ['webgpu'], bytes: 558 * MB },
      { dtype: 'q8', devices: cpu(), bytes: 237 * MB },
    ],
    params: '244M',
    license: 'mit',
    status: 'preview',
    features: ['stream', 'timestamps', 'languages'],
    config: { whisper: true, multilingual: true },
    description: 'Multilingual transcription with timestamps, more accurate than Whisper base.',
  },
  {
    id: 'whisper-large-v3-turbo',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['audio'],
    task: 'speech-to-text',
    source: { repo: 'onnx-community/whisper-large-v3-turbo', revision: '360ebcde2559d60bb474678be3c1de9ef347d01a' },
    variants: [
      { dtype: { encoder_model: 'fp16', decoder_model_merged: 'q4' }, devices: ['webgpu'], shaderF16: true, bytes: 1608 * MB },
      { dtype: { encoder_model: 'q4', decoder_model_merged: 'q4' }, devices: all(), bytes: 759 * MB },
    ],
    params: '809M',
    license: 'mit',
    status: 'preview',
    features: ['stream', 'timestamps', 'languages'],
    config: { whisper: true, multilingual: true },
    description: 'Multilingual transcription with timestamps.',
  },
  // ---------------------------------------------------------------- generate: built-in
  {
    id: 'chrome:gemini-nano',
    version: '1.0.0',
    verb: 'generate',
    accepts: ['text'],
    task: 'chrome-prompt',
    source: { builtin: 'chrome' },
    variants: [{ dtype: 'fp32', devices: ['webgpu', 'wasm'] }],
    requires: { browser: true },
    license: 'google-terms',
    status: 'preview',
    features: ['stream'],
    description: "Chrome's built-in model through the Prompt API. No download for your app.",
  },
  // ---------------------------------------------------------------- evaluate
  {
    id: 'nli-deberta-v3-xsmall',
    version: '1.0.0',
    verb: 'evaluate',
    accepts: ['text'],
    task: 'zero-shot-nli',
    source: { repo: 'Xenova/nli-deberta-v3-xsmall', revision: '2a4f614a701367a02d51389039afc998faeda637' },
    variants: [{ dtype: 'q8', devices: all(), bytes: 87 * MB }],
    params: '22M',
    license: 'apache-2.0',
    status: 'stable',
    features: ['choice', 'score', 'boolean'],
    config: { hypothesis: 'This example is {}.' },
    description: 'Zero-shot judge: scores text against option descriptions with natural-language inference.',
  },
  {
    id: 'prompt-injection-deberta-v3',
    version: '1.0.0',
    verb: 'evaluate',
    accepts: ['text'],
    task: 'sequence-classification',
    source: { repo: 'protectai/deberta-v3-base-prompt-injection-v2', revision: '90c9989b1a342275dd0d1a95aad283c04e075671' },
    variants: [{ dtype: 'fp32', devices: all(), bytes: 739 * MB }],
    params: '184M',
    license: 'apache-2.0',
    status: 'stable',
    features: ['boolean', 'label'],
    config: { positiveLabel: 'INJECTION' },
    description: 'Detects prompt injection and jailbreak attempts.',
  },
  {
    id: 'bert-base-ner',
    version: '1.0.0',
    verb: 'evaluate',
    accepts: ['text'],
    task: 'token-classification',
    source: { repo: 'Xenova/bert-base-NER', revision: '8e892123e8b7c2c0c2bd1dcb598b7d244c4e53aa' },
    variants: [{ dtype: 'q8', devices: all(), bytes: 109 * MB }],
    params: '110M',
    license: 'mit',
    status: 'stable',
    features: ['spans'],
    config: {},
    description: 'Named entities: people, organisations, locations. English.',
  },
  {
    id: 'piiranha-v1',
    version: '1.0.0',
    verb: 'evaluate',
    accepts: ['text'],
    task: 'token-classification',
    source: {
      repo: 'onnx-community/piiranha-v1-detect-personal-information-ONNX',
      revision: '888e40cc88efa70f39a47ce2d5ea789b5e997278',
    },
    variants: [{ dtype: 'q8', devices: all(), bytes: 317 * MB }],
    params: '280M',
    license: 'cc-by-nc-nd-4.0',
    status: 'preview',
    features: ['spans'],
    config: {},
    description: 'PII detection in 6 languages. Non-commercial licence.',
  },
  {
    id: 'lfm2.5-encoder-350m-router',
    version: '1.0.0',
    verb: 'evaluate',
    accepts: ['text'],
    task: 'lfm-router',
    source: { bucket: 'kucukkanat/edgewise-models', path: 'lfm2.5-encoder-350m-router/v1' },
    variants: [
      { dtype: 'q8', devices: all(), bytes: 594 * MB },
      { dtype: 'q4', devices: all(), bytes: 450 * MB },
    ],
    params: '350M',
    license: 'lfm1.0',
    status: 'stable',
    features: ['choice', 'score', 'boolean'],
    config: {
      tokenizer: { repo: 'LiquidAI/LFM2.5-Encoder-350M-Prompt-Router', revision: '35ca4a0469f180f1cf05a630df8842fa17ac18e3' },
      files: { q8: 'onnx/model_q8.onnx', q4: 'onnx/model_q4.onnx' },
      sha256: {
        'onnx/model_q8.onnx': '9c571977677659a1dc6f9e36d69d6285a4666cbfd57083e45b094113375f0f7d',
        'onnx/model_q4.onnx': 'd70042de2a6f44d9c8f0a6c42d801c6d759f24242b8cdf2c9d27f3abc04058a8',
      },
      bos: 1,
    },
    description: 'LFM2.5 zero-shot router: scores text against any lanes in one pass. 15 languages, 8K context.',
  },
  {
    id: 'lfm2.5-encoder-350m-pii',
    version: '1.0.0',
    verb: 'evaluate',
    accepts: ['text'],
    task: 'lfm-token-classification',
    source: { bucket: 'kucukkanat/edgewise-models', path: 'lfm2.5-encoder-350m-pii/v1' },
    variants: [{ dtype: 'q8', devices: all(), bytes: 592 * MB }],
    params: '350M',
    license: 'lfm1.0',
    status: 'stable',
    features: ['spans'],
    config: {
      tokenizer: { repo: 'LiquidAI/LFM2.5-Encoder-350M-PII-Detector', revision: 'b8c9cf3d2d6ae52501b35a27ba46f271449c9ce2' },
      files: { q8: 'onnx/model_q8.onnx' },
      sha256: { 'onnx/model_q8.onnx': '3bfd407e5a47ad84204014a197452381c08acb3f47bb4efde8b83a6462258286' },
      bos: 1,
    },
    description: 'LFM2.5 PII detector: 40 entity types (contact, credential, financial, health and more) in 16 languages.',
  },
  {
    id: 'd1-omni-600m',
    version: '1.0.0',
    verb: 'evaluate',
    accepts: ['text', 'image', 'audio'],
    task: 'd1-decision',
    source: { bucket: 'kucukkanat/edgewise-models', path: 'd1-omni-600m/v1' },
    // 622 MB decision graph; the 108 MB vision and 168 MB audio encoders load on first use.
    variants: [{ dtype: 'q8', devices: all(), bytes: 898 * MB }],
    params: '587M',
    license: 'lfm1.0',
    status: 'stable',
    features: ['choice', 'score', 'boolean'],
    config: {
      tokenizer: { repo: 'LiquidAI/d1-omni-600M', revision: '02b55d7076f15129e59ab3f94783f32c4b088674' },
      files: {
        decide: { q8: 'onnx/decide_q8.onnx' },
        vision: { q8: 'onnx/vision_q8.onnx' },
        audio: { q8: 'onnx/audio_q8.onnx' },
      },
      sha256: {
        'onnx/decide_q8.onnx': 'cab4cd2f59989ca3df8845f0e03cc49294a8f6e06e1ce22d65e47c84e00e7256',
        'onnx/vision_q8.onnx': '9ad9f58e3a64c23ae4f7f95ad40acd45ba1f79962937a4e0c0725144b1c514bb',
        'onnx/audio_q8.onnx': '2bf3cf71459b61448c122cffa62a37e6a1db31b21135a7e699fb3752145cd2cd',
      },
      // config.json's per-type calibration temperatures; they apply to text-only questions.
      temperatures: {
        'choice:11+': 1.372515082359314,
        'choice:2': 1.7465145587921143,
        'choice:3-5': 1.3998981714248657,
        'choice:6-10': 1.1751071214675903,
        'noul:2': 1.6663223505020142,
        'score:3-5': 1.7301132678985596,
        'score:6-10': 1.0,
      },
      maxLength: 16384,
      imageTextLength: 896,
      audioTextLength: 15360,
    },
    description: 'LiquidAI d1 decision model: typed answers over text, images or speech in one pass, zero output tokens.',
  },
  {
    id: 'd1-3b',
    version: '1.0.0',
    verb: 'evaluate',
    accepts: ['text', 'image'],
    task: 'd1-causal',
    source: { bucket: 'kucukkanat/edgewise-models', path: 'd1-3b/v1' },
    // 4.1 GB of decoder, plus a 484 MB vision graph loaded on first use. ONNX Runtime Web cannot allocate the
    // 1.4 GB decoder halves in WebAssembly memory, so it runs on Bun and Node (CPU, or WebGPU through vgpu).
    requires: { server: true },
    variants: [{ dtype: 'q8', devices: ['webgpu', 'cpu'], bytes: 4570 * MB }],
    params: '3.1B',
    license: 'lfm1.0',
    status: 'preview',
    features: ['choice', 'score', 'boolean'],
    config: {
      tokenizer: { repo: 'LiquidAI/d1-3B', revision: '051bcc464b01b9f92942b364d9586b0ef5912432' },
      files: {
        // The embedding table stays full precision: ONNX Runtime quantizes Gather only at 4 bits, which moved
        // answers ten times more than 8-bit everywhere else (scripts/export/README.md).
        embed: { q8: 'onnx/embed.onnx' },
        lower: { q8: 'onnx/lower_q8.onnx' },
        upper: { q8: 'onnx/upper_q8.onnx' },
        vision: { q8: 'onnx/vision_q8.onnx' },
      },
      sha256: {
        'onnx/embed.onnx': 'c3629e51d56d6ddcf14add80a505fd0ca5573335b8048f83e5b457c7d8db2469',
        'onnx/lower_q8.onnx': '0e945e1c3b509476252908d239c9047a551e324b7dff23876015826a7db73ae3',
        'onnx/upper_q8.onnx': 'f4bf268a4c89ff5fa887677c3ad2d549ad6a3557b6e861ed2372d2eaff947f08',
        'onnx/vision_q8.onnx': 'f8ad67db36cf7ac7ea416ec54b9b9d01d5a10cc12720fdcc688afd1bebe15ea4',
      },
      maxLength: 32768,
      imageTokenId: 124907,
      browserAlternative: 'd1-omni-600m',
      hidden: 2048,
    },
    description: 'LiquidAI d1 decision model, 3B: the most accurate d1, over text and images, answered at the last token.',
  },
  // ---------------------------------------------------------------- embed
  {
    id: 'all-minilm-l6-v2',
    version: '1.0.0',
    verb: 'embed',
    accepts: ['text'],
    task: 'feature-extraction',
    source: { repo: 'Xenova/all-MiniLM-L6-v2', revision: '751bff37182d3f1213fa05d7196b954e230abad9' },
    variants: [{ dtype: 'q8', devices: all(), bytes: 23 * MB }],
    params: '22M',
    license: 'apache-2.0',
    status: 'stable',
    features: [],
    config: { pooling: 'mean', dimensions: 384 },
    description: 'Tiny English embedding model. 384 dimensions.',
  },
  {
    id: 'embeddinggemma-300m',
    version: '1.0.0',
    verb: 'embed',
    accepts: ['text'],
    task: 'feature-extraction',
    source: { repo: 'onnx-community/embeddinggemma-300m-ONNX', revision: '5090578d9565bb06545b4552f76e6bc2c93e4a66' },
    // q4 uses GatherBlockQuantized, which the WebAssembly build of ONNX Runtime lacks; q8 works there.
    variants: [
      { dtype: 'q4', devices: ['webgpu', 'cpu'], bytes: 197 * MB },
      { dtype: 'q8', devices: ['wasm'], bytes: 309 * MB },
    ],
    params: '300M',
    license: 'gemma',
    status: 'stable',
    features: ['purpose', 'dimensions'],
    config: {
      pooling: 'sentence_embedding',
      dimensions: 768,
      matryoshka: [768, 512, 256, 128],
      prefixes: { query: 'task: search result | query: ', document: 'title: none | text: ' },
    },
    description: 'Multilingual embeddings. 768 dimensions, shortenable to 128.',
  },
  // ---------------------------------------------------------------- speak
  {
    id: 'kokoro-82m',
    version: '1.0.0',
    verb: 'speak',
    accepts: ['text'],
    task: 'kokoro',
    source: { repo: 'onnx-community/Kokoro-82M-v1.0-ONNX', revision: '1939ad2a8e416c0acfeecc08a694d14ef25f2231' },
    variants: [
      { dtype: 'fp32', devices: ['webgpu'], bytes: 326 * MB, memory: 1.65 * GB },
      { dtype: 'q8', devices: cpu(), bytes: 92 * MB, memory: { wasm: 0.45 * GB } },
    ],
    params: '82M',
    license: 'apache-2.0',
    status: 'stable',
    features: ['voices', 'blend', 'stream'],
    config: { sampleRate: 24000 },
    description: 'High-quality English speech with 28 voices.',
  },
  {
    id: 'chatterbox-turbo',
    version: '1.0.0',
    verb: 'speak',
    accepts: ['text', 'audio'],
    task: 'chatterbox',
    source: { repo: 'ResembleAI/chatterbox-turbo-ONNX', revision: 'd21799bd0354adb85e348b8a0442a8405110a2cf' },
    // Every quantized export uses GatherBlockQuantized, which ONNX Runtime's WebAssembly build lacks,
    // and fp32 is 3.3 GB, so browsers need WebGPU.
    variants: [{ dtype: { embed_tokens: 'q4', speech_encoder: 'q4', model: 'q4', conditional_decoder: 'q4' }, devices: ['webgpu', 'cpu'], bytes: 717 * MB }],
    params: '350M',
    license: 'mit',
    status: 'preview',
    features: ['clone', 'stream'],
    config: { sampleRate: 24000, minReferenceSeconds: 3, maxReferenceSeconds: 10 },
    description: 'Zero-shot voice cloning from 3 to 10 seconds of reference audio. English. Needs WebGPU in browsers.',
  },
  // ---------------------------------------------------------------- paint
  {
    id: 'sd-turbo',
    version: '1.0.0',
    verb: 'paint',
    accepts: ['text'],
    task: 'sd-turbo',
    source: { repo: 'schmuell/sd-turbo-ort-web', revision: 'ace89b7d2cd849f9a73914cdbb8a3ea60c853dd1' },
    variants: [
      { dtype: 'fp16', devices: ['webgpu'], shaderF16: true, bytes: 2582 * MB },
      { dtype: 'fp16', devices: ['cpu'], bytes: 2582 * MB },
    ],
    params: '1B',
    license: 'stabilityai-community',
    status: 'preview',
    features: ['seed', 'steps'],
    config: { latentChannels: 4, vaeScale: 0.18215, sigma: 14.6146 },
    description: 'One-step image generation at 512×512.',
  },
  // ---------------------------------------------------------------- forecast
  {
    id: 'chronos-bolt-tiny',
    version: '1.0.0',
    verb: 'forecast',
    accepts: ['series'],
    task: 'chronos-bolt',
    source: { bucket: 'kucukkanat/edgewise-models', path: 'chronos-bolt-tiny/v1' },
    variants: [{ dtype: 'fp32', devices: all(), bytes: 35 * MB }],
    params: '9M',
    license: 'apache-2.0',
    status: 'stable',
    features: ['quantiles'],
    config: {
      file: 'onnx/model.onnx',
      sha256: { 'onnx/model.onnx': 'fee438f848e17a8d4f88ffb2f3fe9b7a4773479e60386f1c776a099938f0bb1f' },
      quantiles: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9],
      predictionLength: 64,
      contextLength: 2048,
      patchSize: 16,
    },
    description: 'Zero-shot time-series forecaster (Amazon Chronos-Bolt tiny). 64 steps per pass, 9 quantiles.',
  },
  {
    id: 'chronos-bolt-small',
    version: '1.0.0',
    verb: 'forecast',
    accepts: ['series'],
    task: 'chronos-bolt',
    source: { bucket: 'kucukkanat/edgewise-models', path: 'chronos-bolt-small/v1' },
    variants: [{ dtype: 'fp32', devices: all(), bytes: 191 * MB }],
    params: '48M',
    license: 'apache-2.0',
    status: 'stable',
    features: ['quantiles'],
    config: {
      file: 'onnx/model.onnx',
      sha256: { 'onnx/model.onnx': '7bd6dfaba806ae284b198b14094ef97244f373733a777c26c1c559cbff66ca27' },
      quantiles: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9],
      predictionLength: 64,
      contextLength: 2048,
      patchSize: 16,
    },
    description: 'Larger Chronos-Bolt with better accuracy on complex series.',
  },
  // ---------------------------------------------------------------- vad
  {
    id: 'silero-vad',
    version: '1.0.0',
    verb: 'vad',
    accepts: ['audio'],
    task: 'silero-vad',
    source: { repo: 'onnx-community/silero-vad', revision: 'e71cae966052b992a7eca6b17738916ce0eca4ec' },
    variants: [{ dtype: 'fp32', devices: cpu(), bytes: 2 * MB, memory: { wasm: 0.1 * GB } }],
    params: '2M',
    license: 'mit',
    status: 'stable',
    config: { sampleRate: 16000, frame: 512 },
    description: 'Voice activity detection used by mic() and live transcription.',
  },
];

export const builtinAliases: Record<string, string> = {
  'text:tiny': 'lfm2.5-230m',
  'text:default': 'lfm2.5-350m',
  'vision:default': 'lfm2.5-vl-450m',
  'vision:fast': 'florence-2-base',
  'stt:realtime': 'moonshine-base',
  'stt:tiny': 'moonshine-tiny',
  'stt:accurate': 'whisper-large-v3-turbo',
  'stt:multilingual': 'whisper-base',
  'judge:router': 'nli-deberta-v3-xsmall',
  'judge:injection': 'prompt-injection-deberta-v3',
  'judge:entities': 'bert-base-ner',
  'judge:pii': 'lfm2.5-encoder-350m-pii',
  'judge:omni': 'd1-omni-600m',
  'judge:large': 'd1-3b',
  'embed:default': 'embeddinggemma-300m',
  'embed:tiny': 'all-minilm-l6-v2',
  'voice:default': 'kokoro-82m',
  'voice:clone': 'chatterbox-turbo',
  'image:default': 'sd-turbo',
  'vad:default': 'silero-vad',
  'forecast:default': 'chronos-bolt-tiny',
};
